import { TRPCClientError } from '@trpc/client';

// Permission display names (German)
export const PERMISSION_LABELS: Record<string, string> = {
    READ_PATIENT_MEDICAL_RECORD: 'Patientenakte lesen',
    WRITE_PATIENT_MEDICAL_RECORD: 'Patientenakte bearbeiten',
    DELETE_PATIENT_MEDICAL_RECORD: 'Patientenakte löschen',
    READ_PATIENT_BLOOD_RESULTS: 'Blutergebnisse lesen',
    WRITE_PATIENT_BLOOD_RESULTS: 'Blutergebnisse bearbeiten',
    READ_INVOICES: 'Rechnungen lesen',
    WRITE_INVOICES: 'Rechnungen bearbeiten',
    READ_APPOINTMENTS: 'Termine lesen',
    WRITE_APPOINTMENTS: 'Termine bearbeiten',
    READ_CUSTOMERS: 'Kunden lesen',
    WRITE_CUSTOMERS: 'Kunden bearbeiten',
    MANAGE_PERMISSIONS: 'Berechtigungen verwalten',
    MANAGE_USERS: 'Benutzer verwalten',
    MANAGE_ROLES: 'Rollen verwalten',
};

/**
 * Get user-friendly display name for a permission.
 */
export const getPermissionLabel = (permission: string): string => {
    return PERMISSION_LABELS[permission] || permission;
};

/**
 * Error cause type for permission denied errors.
 */
type PermissionDeniedCause = {
    type: 'PERMISSION_DENIED';
    requiredPermissions: string[];
    missingPermissions: string[];
    userPermissions: string[];
};

/**
 * Check if an error is an access denied (FORBIDDEN) error from tRPC.
 */
export const isAccessDeniedError = (error: unknown): boolean => {
    if (error instanceof TRPCClientError) {
        return error.data?.code === 'FORBIDDEN';
    }
    return false;
};

/**
 * Check if an error is specifically a permission denied error (vs other FORBIDDEN errors).
 */
export const isPermissionDeniedError = (error: unknown): boolean => {
    if (error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN') {
        const cause = error.data?.cause as PermissionDeniedCause | undefined;
        return cause?.type === 'PERMISSION_DENIED';
    }
    return false;
};

/**
 * Extract the access denied reason from a tRPC error.
 */
export const getAccessDeniedReason = (error: unknown): string | undefined => {
    if (error instanceof TRPCClientError) {
        const cause = error.data?.cause as { reason?: string } | undefined;
        return cause?.reason || error.message;
    }
    return undefined;
};

/**
 * Extract missing permissions from a permission denied error.
 */
export const getMissingPermissions = (error: unknown): string[] | undefined => {
    if (error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN') {
        const cause = error.data?.cause as PermissionDeniedCause | undefined;
        if (cause?.type === 'PERMISSION_DENIED') {
            return cause.missingPermissions;
        }
    }
    return undefined;
};

/**
 * Extract required permissions from a permission denied error.
 */
export const getRequiredPermissions = (error: unknown): string[] | undefined => {
    if (error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN') {
        const cause = error.data?.cause as PermissionDeniedCause | undefined;
        if (cause?.type === 'PERMISSION_DENIED') {
            return cause.requiredPermissions;
        }
    }
    return undefined;
};

/**
 * Get a user-friendly message describing what permissions are missing.
 */
export const getPermissionDeniedMessage = (error: unknown): string => {
    const missingPermissions = getMissingPermissions(error);
    
    if (missingPermissions && missingPermissions.length > 0) {
        const permissionLabels = missingPermissions.map(getPermissionLabel);
        if (permissionLabels.length === 1) {
            return `Ihnen fehlt die Berechtigung: ${permissionLabels[0]}`;
        }
        return `Ihnen fehlen die Berechtigungen: ${permissionLabels.join(', ')}`;
    }
    
    return getAccessDeniedReason(error) || 'Sie haben keinen Zugriff auf diese Daten';
};
